import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { getProduct } from "@/lib/products";

const Stars = ({ value, onChange, testid }) => (
  <div className="flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button type="button" key={n} onClick={() => onChange(n)} aria-label={`${n} stars`} data-testid={`${testid}-${n}`}
        className="transition-transform duration-200 hover:scale-110">
        <Star className="w-7 h-7" strokeWidth={1.4} fill={n <= value ? "#D88B94" : "transparent"} color={n <= value ? "#D88B94" : "#B9A49C"} />
      </button>
    ))}
  </div>
);

const slug = (i) => `${i.product_id}-${i.shade.toLowerCase().replace(/\s+/g, "-")}`;

export default function ReviewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});
  const [state, setState] = useState("idle");

  useEffect(() => {
    http.get(`/reviews/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [token]);

  const items = Array.isArray(data?.items) ? data.items : [];
  const set = (k, patch) => setForm((f) => ({ ...f, [k]: { rating: 0, comment: "", ...f[k], ...patch } }));

  const submit = async (e) => {
    e.preventDefault();
    const reviews = items.filter((i) => form[slug(i)]?.rating).map((i) => ({ product_id: i.product_id, shade: i.shade, ...form[slug(i)] }));
    if (!reviews.length) return toast.error("Tap the stars to rate at least one shade");
    setState("loading");
    try {
      await http.post(`/reviews/${token}`, { reviews });
      setState("done");
    } catch (err) {
      setState("idle");
      toast.error(formatApiError(err));
    }
  };

  const done = state === "done" || data?.reviewed;
  return (
    <main className="min-h-screen bg-[#F9ECE8] pt-[110px] pb-24 px-5" data-testid="review-page">
      <div className="max-w-2xl mx-auto">
        {error ? (
          <p className="font-display text-4xl text-center pt-16" data-testid="review-error">{error}</p>
        ) : !data ? (
          <Loader2 className="w-6 h-6 animate-spin mx-auto mt-24" />
        ) : done ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center pt-16" data-testid="review-thanks">
            <h1 className="font-display text-5xl sm:text-6xl leading-[0.95]">Thank you. <span className="italic text-[#D88B94]">Truly.</span></h1>
            <p className="text-[#6E504A] mt-6">Your review helps others find their perfect shade.</p>
            <Link to="/" className="inline-flex mt-10 rounded-full bg-[#281715] text-[#FAF7F2] px-8 h-12 items-center text-sm hover:bg-[#4A2E2B] transition-colors duration-200" data-testid="review-home-link">Back to LUMI</Link>
          </motion.div>
        ) : (
          <form onSubmit={submit} data-testid="review-form">
            <p className="overline text-[#6E504A]">Rate your shade</p>
            <h1 className="font-display text-5xl sm:text-6xl mt-4 leading-[0.95]">How's it wearing{data.first_name ? `, ${data.first_name}` : ""}?</h1>
            <div className="mt-10 space-y-4">
              {items.map((i) => {
                const k = slug(i);
                const p = getProduct(i.product_id);
                return (
                  <div key={k} className="rounded-[28px] bg-[#FAF7F2] border border-[#E6D7CC] p-5 sm:p-6 flex gap-5" data-testid={`review-item-${k}`}>
                    {p && <img src={p.image} alt={i.name} className="w-20 h-24 object-cover rounded-t-full rounded-b-2xl shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-2xl">{i.name}</p>
                      <p className="text-sm text-[#6E504A]">{i.shade}</p>
                      <div className="mt-3"><Stars value={form[k]?.rating ?? 0} onChange={(n) => set(k, { rating: n })} testid={`stars-${k}`} /></div>
                      <textarea value={form[k]?.comment ?? ""} onChange={(e) => set(k, { comment: e.target.value })} maxLength={600} rows={2}
                        placeholder="How does the shade look on you? (optional)" data-testid={`comment-${k}`}
                        className="mt-3 w-full rounded-2xl border border-[#E6D7CC] bg-white px-4 py-3 text-sm outline-none focus:border-[#4A2E2B]" />
                    </div>
                  </div>
                );
              })}
            </div>
            <button disabled={state === "loading"} data-testid="review-submit-btn"
              className="mt-8 w-full h-14 rounded-full bg-[#281715] text-[#FAF7F2] flex items-center justify-center hover:bg-[#4A2E2B] transition-colors duration-200">
              {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send my review"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
